import React, { useState } from 'react';
import { BACKEND_URL } from '../config/api';
import './UploadResume.css';

export default function UploadResume({ onUploadSuccess }) {
  const [fileName, setFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedInfo, setUploadedInfo] = useState(null);
  const [error, setError] = useState(null);

  function onFileChange(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (f.type !== 'application/pdf') {
      setError('Only PDF files are allowed. Please upload a PDF resume.');
      setSelectedFile(null);
      setFileName('');
      setUploadedInfo(null);
      return;
    }
    setSelectedFile(f);
    setFileName(f.name);
    setUploadedInfo(null);
    setError(null);
  }

  async function onUpload() {
    if (!selectedFile) return setError('No file selected');
    setUploading(true);
    setError(null);

    try {
      const email = localStorage.getItem('userEmail');
      if (!email) {
        setError('Missing user email. Please sign in again.');
        setUploading(false);
        return;
      }

      // Upload resume to backend API (which internally calls OpenAI APIs)
      const resumeApiUrl = `${BACKEND_URL}/api/resumes/upload?email=${encodeURIComponent(email)}`;
      
      // Send file as form-data
      const resumeFormData = new FormData();
      resumeFormData.append('file', selectedFile);
      // Add meta JSON as required by backend
      resumeFormData.append('meta', JSON.stringify({
        filename: selectedFile.name,
        type: selectedFile.type,
      }));

      // Cookie + token auth: credentials: 'include' sends JWT cookie, header sends token
      const headers = {};
      const authToken = localStorage.getItem('auth_token');
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      
      const resumeRes = await fetch(resumeApiUrl, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: resumeFormData
      });

      if (!resumeRes.ok) {
        const text = await resumeRes.text();
        console.error('Resume upload failed:', resumeRes.status, text);
        if (resumeRes.status === 401 || resumeRes.status === 403) {
          throw new Error('Unauthorized. Please sign in again.');
        }
        let parsed;
        try {
          parsed = JSON.parse(text);
        } catch {
          parsed = { raw: text };
        }
        throw new Error(parsed.error || parsed.message || parsed.raw || 'Upload failed');
      }

      const resumeData = await resumeRes.json();

      setUploadedInfo(resumeData);
      localStorage.setItem('uploaded_resume_name', resumeData.filename || fileName);
      if (onUploadSuccess) onUploadSuccess(resumeData);
      setUploading(false);
    } catch (err) {
      console.error('Upload error:', err);
      const message = typeof err === 'string' ? err : (err.message || 'Upload failed');
      setError(message);
      setUploading(false);
    }
  }

  return (
    <section id="upload-section" className="upload-resume">
      <h3>Upload your CV / Resume</h3>
      <p className="muted">Upload a <b>PDF</b> resume only. Other file types are not allowed.</p>

      <div className="uploader">
        <label className="upload-btn">
          <input type="file" accept=".pdf" onChange={onFileChange} />
          <span>{selectedFile ? 'Change file' : 'Choose File'}</span>
        </label>

        <div className="filename">{fileName}</div>

        <button className="upload-btn-action" onClick={onUpload} disabled={uploading || !selectedFile}>
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
      </div>

      {error && (
        <div style={{ color: '#ffb4b4', marginTop: 12 }}>
          {typeof error === 'string' ? error : JSON.stringify(error, null, 2)}
        </div>
      )}
      {uploadedInfo && (
        <div style={{ marginTop: 12, color: '#bde0fe' }}>
          <div>Uploaded: <strong>{uploadedInfo.filename}</strong></div>
          {uploadedInfo.url && (
            <div>
              <a href={uploadedInfo.url} target="_blank" rel="noreferrer">View stored resume</a>
            </div>
          )}
          {uploadedInfo.openAi && (
            <div style={{ marginTop: 8, fontSize: '0.85rem' }}>
              <div>OpenAI File ID: <code>{uploadedInfo.openAi.id}</code></div>
              <div>Status: {uploadedInfo.openAi.status}</div>
              <div>Bytes: {uploadedInfo.openAi.bytes}</div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
